// ViewSession.jsx
import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db, storage } from '../../firebase';
import {
  doc,
  getDoc,
  updateDoc,
  deleteField,
  Timestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useNavigate, useParams } from 'react-router';
import dayjs from 'dayjs';
import * as theme from '../../theme';
import Heading from '../../components/Heading';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import ErrorDisplay from '../../components/ErrorDisplay';
import TruCard from '../../components/TruCard';
import FileUploadLink from '../../components/fileUpload/FileUploadLink';
// import FileUploadContainer from '../../components/fileUpload/FileUploadContainer'; // No longer needed for modal
import ConfirmationModal from '../../components/ConfirmationModal'; // assuming it's in components
import { extractPdf, extractPdfWellness } from '../../functions/endpoints/ExtractPDF';
import { generateClientReportHTML } from '../clients/scans/reports/ClientReport';
import { generateWellnessReportHTML } from '../clients/scans/reports/WellnessReport';
import { generateEmotionsReportHTML } from '../clients/scans/reports/EmotionsReport';
import { generateSupplementScheduleHTML } from '../clients/scans/reports/SupplementSchedule';

export default function ViewSession() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [session, setSession] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Upload modal state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadType, setUploadType] = useState(''); // 'clientReport' | 'wellness' | 'emotions'
  const [modalFile, setModalFile] = useState(null);
  const [modalError, setModalError] = useState('');

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(''); // 'wellness' | 'emotions'
  const [deleteError, setDeleteError] = useState('');

  const clearError = () => setError('');

  useEffect(() => {
    if (sessionId) {
      getSession();
    }
  }, [sessionId]);

  const getSession = async () => {
    setLoading(true);
    try {
      const sessionRef = doc(db, 'oneOffReports', sessionId);
      const snap = await getDoc(sessionRef);
      if (snap.exists()) {
        setSession({ id: snap.id, ...snap.data() });
      } else {
        setError('Session not found.');
      }
    } catch (err) {
      setError('Failed to load session: ' + err.message);
    }
    setLoading(false);
  };

  // Download PDF (if no grok data)
  const downloadFile = async (fileName) => {
    const newWindow = window.open('', '_blank');
    if (!newWindow) {
      setError('Popup blocked. Please allow popups.');
      return;
    }
    try {
      const filePath = `oneOffReports/${sessionId}/${fileName}`;
      const fileRef = ref(storage, filePath);
      const url = await getDownloadURL(fileRef);
      newWindow.location.href = url;
    } catch (err) {
      setError('Failed to download file: ' + err.message);
      newWindow.close();
    }
  };

  // View report (grok HTML or PDF)
  const handleViewReport = (reportKey) => {
    let report = {};
    if (reportKey !== 'clientReport' && reportKey !== 'supplementSchedule') {
      report = session.reports?.[reportKey];
      if (!session.reports?.[reportKey]) return;
    }

    const grokData = reportKey === 'clientReport' ? session.reportData || {} : session.reportData?.[reportKey] || {};
    const fileName = report.fileName || '';
    const brand = session.brand || 'RenuHealth';

    let html = '';
    if (Object.keys(grokData).length > 0) {
      // Has processed Grok data → render HTML
      if (reportKey === 'clientReport') {
        html = generateClientReportHTML(
          grokData,
          `${session.firstName} ${session.lastName}`,
          `${dayjs(session.sessionDate).format('YYYY.MM.DD')}`,
          brand
        );
      } else if (reportKey === 'supplementSchedule') {
        html = generateSupplementScheduleHTML(grokData, `${session.firstName} ${session.lastName}`, `${dayjs(session.sessionDate).format('YYYY.MM.DD')}`, brand);
      } else if (reportKey === 'wellness') {
        html = generateWellnessReportHTML(grokData, `${session.firstName} ${session.lastName}`, `${dayjs(session.sessionDate).format('YYYY.MM.DD')}`, brand);
      } else if (reportKey === 'emotions') {
        html = generateEmotionsReportHTML(grokData, brand);
      }
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(html);
        win.document.close();
      }
    } else {
      if (reportKey === 'clientReport') {
        setError('No client report data available.');
        return;
      } else if (fileName) {
        downloadFile(fileName);
      } else {
        setError('No report available.');
      }
    }
  };

  // Modal file handlers
  const handleModalFileSelect = (e) => {
    const selected = e?.target?.files[0];
    if (selected) setModalFile(selected);
  };

  const handleModalClearFile = () => setModalFile(null);

  const handleModalUpload = async () => {
    if (!modalFile || !uploadType) {
      setModalError('Please select a file.');
      return;
    }
    setModalError('');
    setLoading(true);

    const storagePath = `oneOffReports/${sessionId}/${modalFile.name}`;
    const fileRef = ref(storage, storagePath);
    const timestamp = dayjs().toISOString();

    try {
      await uploadBytes(fileRef, modalFile);

      const reportKey =
        uploadType === 'clientReport'
          ? 'clientReport'
          : uploadType === 'wellness'
          ? 'wellness'
          : 'emotions';

      const updatePayload = {
        [`reports.${reportKey}`]: {
          fileName: modalFile.name,
          dateUploaded: timestamp,
          uploadedBy: currentUser.uid,
        },
        dateModified: timestamp,
      };

      await updateDoc(doc(db, 'oneOffReports', sessionId), updatePayload);

      // Fire extraction for wellness / emotions
      if (uploadType === 'wellness') {
        extractPdfWellness({
          pdfPath: storagePath,
          isOneOff: true,
          reportId: sessionId,
        }).catch((err) => console.error('Wellness extraction failed:', err));
      } else if (uploadType === 'emotions') {
        extractPdf({
          pdfPath: storagePath,
          isOneOff: true,
          reportId: sessionId,
        }).catch((err) => console.error('Emotions extraction failed:', err));
      }

      // Refresh session
      await getSession();

      // Close modal
      setModalFile(null);
      setUploadModalOpen(false);
    } catch (err) {
      setModalError('Upload failed: ' + err.message);
    }
    setLoading(false);
  };

  const openUploadModal = (type) => {
    setUploadType(type);
    setModalFile(null);
    setModalError('');
    setUploadModalOpen(true);
  };

  const openDeleteModal = (type) => {
    setDeleteType(type);
    setDeleteError('');
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteType) return;
    setDeleteError('');
    setLoading(true);

    try {
      const reportKey = deleteType === 'wellness' ? 'wellness' : 'emotions';
      const report = session.reports?.[reportKey];

      // Delete file from storage if it exists
      if (report?.fileName) {
        const storagePath = `oneOffReports/${sessionId}/${report.fileName}`;
        const fileRef = ref(storage, storagePath);
        try {
          await deleteObject(fileRef);
        } catch (err) {
          console.warn('Storage delete failed (file may not exist):', err);
        }
      }

      // Remove report metadata and processed data
      const updatePayload = {
        [`reports.${reportKey}`]: deleteField(),
        [`reportData.${reportKey}`]: deleteField(),
        dateModified: dayjs().toISOString(),
      };

      await updateDoc(doc(db, 'oneOffReports', sessionId), updatePayload);

      // Refresh session
      await getSession();

      setDeleteModalOpen(false);
    } catch (err) {
      setDeleteError('Delete failed: ' + err.message);
    }
    setLoading(false);
  };

  if (loading && !session.id) {
    return (
      <div style={theme.Container.basic}>
        <Heading title="Loading Session..." />
      </div>
    );
  }

  const name = `${session.firstName || ''} ${session.lastName || ''}`.trim();
  const sessionDateFormatted = session.sessionDate
    ? dayjs(session.sessionDate).format('MMMM D, YYYY')
    : 'No date';

  return (
    <div style={theme.Container.flexColumnContainer}>
      <ErrorDisplay message={error} type="error" clearMessage={clearError} />

      <div style={theme.Container.flexColumn}>
        <Heading
          title={`${session.firstName || ''} ${session.lastName || ''}`}
          onClick={() => navigate(`/admin/reports`)}
        />
      </div>
      <div style={theme.Container.fullMax}>
        <div style={theme.Container.flexOne}>
          <Heading
            title={dayjs(session.sessionDate).format('MMMM D, YYYY')}
            subHeading={true}
          />

      <TruCard title="Reports" styles={{ marginTop: 20 }}>
        {/* Client Report */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <FileUploadLink
            label={name}
            content={dayjs(session.sessionDate).format('MM/DD/YYYY')}
            onClick={() => handleViewReport('clientReport')}
          />
          <Button
            variant="contained"
            color="secondary"
            onClick={() => navigate(`/admin/reports/clientReport/${session.id}/edit`)}
            sx={{ ml: 2, width: 'fit-content' }}
          >
            Edit
          </Button>
        </Box>

        {/* Supplements Schedule */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <FileUploadLink
              label="Supplement Schedule"
              content={dayjs(session.sessionDate).format('MM/DD/YYYY')}
              onClick={() => handleViewReport('supplementSchedule')}
            />
            <Button
              variant="contained"
              color="secondary"
              onClick={() => navigate(`/admin/reports/supplements/${session.id}/edit`)}
              sx={{ ml: 2, width: 'fit-content' }}
            >
              Edit
            </Button>
          </Box>

        {/* Wellness Scan */}
        {session.reports?.wellness ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <FileUploadLink
              label="Wellness Scan"
              content={dayjs(session.reports.wellness.dateUploaded).format('MM/DD/YYYY h:mm A')}
              onClick={() => handleViewReport('wellness')}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => navigate(`/admin/reports/wellness/${session.id}/edit`)}
                sx={{ width: 'fit-content' }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => openDeleteModal('wellness')}
              >
                Delete
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <FileUploadLink 
              label="Wellness Scan" 
              content="Not Uploaded" 
              onClick={() => {}}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => openUploadModal('wellness')}
              sx={{ ml: 2 }}
            >
              Upload
            </Button>
          </Box>
        )}

        {/* Emotions */}
        {session.reports?.emotions ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FileUploadLink
              label="Emotions"
              content={dayjs(session.reports.emotions.dateUploaded).format('MM/DD/YYYY h:mm A')}
              onClick={() => handleViewReport('emotions')}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => navigate(`/admin/reports/emotions/${session.id}/edit`)}
                sx={{ width: 'fit-content' }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => openDeleteModal('emotions')}
              >
                Delete
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FileUploadLink 
              label="Emotions" 
              content="Not Uploaded" 
              onClick={() => {}}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => openUploadModal('emotions')}
              sx={{ ml: 2 }}
            >
              Upload
            </Button>
          </Box>
        )}
      </TruCard>

      {/* Upload Modal */}
      <ConfirmationModal
        open={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setModalFile(null);
          setModalError('');
        }}
        onConfirm={handleModalUpload}
        title={`Upload ${uploadType === 'clientReport' ? 'Client Report' : uploadType === 'wellness' ? 'Wellness Scan' : 'Emotions Report'}`}
        description="Select a PDF file to upload for this session."
        confirmButtonText={loading ? 'Uploading...' : 'Upload Report'}
        error={modalError}
        isWide={true}
        hideCancelButton={false}
      >
        <Box sx={{ mt: 2 }}>
          <InputLabel>Upload PDF Report</InputLabel>
          <input
            type="file"
            accept="application/pdf"
            onChange={handleModalFileSelect}
            style={{ display: 'block', marginBottom: 10 }}
          />
          {modalFile && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <span>Selected: {modalFile.name}</span>
              <Button onClick={handleModalClearFile} color="error" sx={{ ml: 2 }}>
                Remove
              </Button>
            </Box>
          )}
        </Box>
      </ConfirmationModal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeleteError('');
        }}
        onConfirm={handleDelete}
        title={`Delete ${deleteType === 'wellness' ? 'Wellness Scan' : 'Emotions Report'}`}
        description="This action cannot be undone. The uploaded PDF and all processed data will be permanently deleted."
        confirmButtonText={loading ? 'Deleting...' : 'Confirm Delete'}
        error={deleteError}
        isWide={true}
        hideCancelButton={false}
      />
      </div>
      </div>
    </div>
  );
}